/**
 * OnboardingShell — layout wrap per le pagine del portale paziente.
 *
 * Header: logo testuale "Dr.ssa Stucchi Emanuela - Studio Dietetico"
 * (no immagine per evitare flash). Mobile-first padding 16px, desktop 32px.
 * Container max-width 600px per leggibilita' form.
 * Footer: contatti studio + privacy.
 *
 * Componente passivo: niente router, niente fetch. Riceve children e
 * opzionalmente currentStep (per ProgressBar).
 */

import type { ReactNode } from 'react';
import { ProgressBar } from './ProgressBar';

interface OnboardingShellProps {
  children: ReactNode;
  /** Se presente, mostra ProgressBar con step corrente (1..4). */
  currentStep?: number;
  /** Titolo H1 della pagina (opzionale). */
  title?: string;
  /** Sottotitolo (opzionale, sotto H1). */
  subtitle?: string;
}

export function OnboardingShell({
  children,
  currentStep,
  title,
  subtitle,
}: OnboardingShellProps) {
  return (
    <div className="min-h-screen flex flex-col bg-bg-app text-text-main">
      <header className="border-b border-outline-variant bg-surface-container-lowest">
        <div className="max-w-[600px] mx-auto px-4 md:px-8 py-4 md:py-6 text-center">
          <p className="font-semibold text-text-main text-base md:text-lg">
            Dr.ssa Stucchi Emanuela
          </p>
          <p className="text-xs md:text-sm text-text-secondary">
            Studio Dietetico
          </p>
        </div>
        {currentStep !== undefined && (
          <div className="bg-surface-container-lowest pb-4">
            <ProgressBar currentStep={currentStep} />
          </div>
        )}
      </header>

      <main
        id="main-content"
        className="flex-1 w-full max-w-[600px] mx-auto px-4 md:px-8 py-6 md:py-10"
      >
        {title && (
          <h1 className="text-2xl md:text-3xl font-semibold text-text-main mb-2">
            {title}
          </h1>
        )}
        {subtitle && (
          <p className="text-text-secondary mb-6 text-sm md:text-base">
            {subtitle}
          </p>
        )}
        {children}
      </main>

      <footer className="border-t border-outline-variant bg-surface-container-lowest mt-auto">
        <div className="max-w-[600px] mx-auto px-4 md:px-8 py-4 text-xs text-text-secondary">
          <p className="mb-1">
            <strong>Studio Dr.ssa Stucchi Emanuela</strong> &middot; Grezzago (MI)
          </p>
          <p>
            Per assistenza:{' '}
            <a
              href="mailto:stucchi@studiodietetico.eu"
              className="text-primary underline"
            >
              stucchi@studiodietetico.eu
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
